import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Col, Row} from "reactstrap";
import {CheckboxForm} from "../../../../base/checkbox/checkbox";
import * as Yup from "yup";
import ValidateMessages from "../../../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import Form from "../../../../base/form";
import {Divider, Fade} from "@mui/material";
import Button from "../../../../base/button/button";
import NCRInput from "../../../../base/input/ncr-input";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {MuiRFF} from '../../../../form';
import IconButton from "../../../../base/button/icon_button";
import {OpenInNew} from "@mui/icons-material";
import {routeFunctions} from "../../../../../../core/models/static/routes/app-routes";
import {openLinkInNewTab} from "../../../../../../core/services/utils/utils";
import DateUtils from "../../../../../../core/services/date-utils";
import {OutlinedInputForm} from "../../../../base/input/outlined_input";
import OperatorAutocomplete from "../../../../text-fields/autocomplete/operator-autocomplete";
import RgaAutocomplete from "../../../../text-fields/autocomplete/rga-autocomplete";

const schema = Yup.object().shape({
    manualNcr: Yup.boolean().default(true).nullable(),
    todaySubmission: Yup.boolean().default(true).required(ValidateMessages.required),
    inventoryBased: Yup.boolean().default(false).required(ValidateMessages.required),
    description: Yup.string().trim().nullable().required(ValidateMessages.required),
    rgaNumber: Yup.string().trim().nullable(),
    reportedBy: Yup.object().nullable().required(ValidateMessages.required),
    // assignedTo: Yup.object().nullable(),
    ncr: Yup.string()
        .trim()
        .when("manualNcr", {
            is: (val) => !!val,
            then: Yup.string().trim().nullable().required(ValidateMessages.required),
            otherwise: Yup.string().nullable(),
        }),
});

const AddNCRBasicInfoSection = ({submit, basicInfoValues, updating}) => {
    const validate = makeValidate(schema);
    const required = makeRequired(schema);
    const [validatingNCR, setValidatingNCR] = useState(false);
    const [duplicateNCR, setDuplicateNCR] = useState();
    const [duplicatedNCRs, setDuplicatedNCRs] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();
    const ncrInput = useRef();

    const initialValues = useMemo(() => ({
        manualNcr: basicInfoValues?.manualNcr,
        rgaNumber: basicInfoValues?.rgaNumber,
        ncr: basicInfoValues?.ncr,
        reportedBy: basicInfoValues?.reportedBy,
        // assignedTo: basicInfoValues?.assignedTo,
        submissionDate: basicInfoValues?.submissionDate ? DateUtils.serverToUTC(basicInfoValues?.submissionDate) : undefined,
        todaySubmission: basicInfoValues?.todaySubmission,
        description: basicInfoValues?.description,
    }), [basicInfoValues]);

    /**
     * Listens for the changes in defineValues and with each change:
     * - sets the initial values of the form.
     */
    useEffect(() => {
        ncrInput.current?.resetValidation();
    }, [basicInfoValues]);

    /**
     * Sets the defined values with the current form values and then proceeds to the lookup section of the dialog.
     */
    const proceedToNextStep = useCallback(async (values) => {
        setSubmitting(true);
        await submit({
            ...values,
            submissionDate: values?.submissionDate ? DateUtils.UTCToServer(values?.submissionDate)?.format(DateUtils.withoutOffsetFormat) : undefined,
        })
        if (!isMounted())
            return;
        setSubmitting(false);
    }, [submit, isMounted])

    /**
     * If checked sets the manualRga to true and removes the ncr from values of form, otherwise substitutes the ncr
     * with its initial value.
     * @param e
     * @param form
     */
    const onManualNcrChanged = (e, form) => {
        if (!e.target.checked) {
            form.change("ncr", null);
            form.change("manualNcr", true);
        } else {
            form.change("ncr", initialValues.ncr);
        }
        setDuplicateNCR(undefined);
        ncrInput.current?.resetValidation();
        form.change(e.target.name, e.target.checked);
        setTimeout(() => {
            if (!isMounted()) return;
            form.resetFieldState("ncr");
            ncrInput.current?.resetValidation();
        }, 100);
    };

    /**
     * If checked sets the todaySubmission to false and removes the submissionDate from values of form, otherwise substitutes the ncr
     * with its initial value.
     * @param e
     * @param form
     */
    const onTodaySubmissionChanged = (e, form) => {
        if (!e.target.checked) {
            // If "Today" checkbox is unchecked, reset NCR date and sets the todaySubmission to false
            form.change("submissionDate", initialValues.submissionDate);
            form.change("todaySubmission", false);
        } else {
            // If "Today" checkbox is checked, use the initial submission date and hide date picker
            form.change("submissionDate", undefined);
            form.change("todaySubmission", true);
        }
    };

    /**
     * Opens the part support request associated with the duplicated RGA.
     */
    const openNRCInNewTab = () => {
        const newTabUrl = window.location.origin + routeFunctions.ncr.single(duplicateNCR?.id);
        openLinkInNewTab(newTabUrl);
    };

    /**
     * Changes the NCR field's value. If the value can be found in the duplicatedRGAs then sets the duplicated rga
     * @param e {InputEvent}
     * @param form {any}
     */
    const onNCRChanged = (e, form) => {
        const found = duplicatedNCRs?.find((ncr) => ncr.code === e.target.value);
        if (found) {
            setDuplicateNCR(found);
        }
        form.change(e.target.name, e.target.value);
    };

    /**
     * Sets the state associated with the found duplicated ncr
     * @param ncr the ncr info
     */
    const onDuplicateFound = (ncr) => {
        setDuplicateNCR(ncr);
        setDuplicatedNCRs((prevState) => Array.from(new Set([...prevState, ncr]).values()));
    };

    return (
        <div className=" w-100">
            <Form
                className="w-100"
                onSubmit={proceedToNextStep}
                validate={validate}
                initialValues={initialValues}
                render={({values, form}) => {
                    const submitButtonDisabled = !!duplicateNCR || validatingNCR || (values?.manualNcr && values?.ncr === undefined) || submitting || (!values?.todaySubmission && values?.submissionDate === null) || !values?.reportedBy
                    return (
                        <Row noGutters className=" d-flex flex-column w-100">
                            <div className="d-flex flex-column pr-4 pl-4">
                                <Col xs={12} className="d-flex flex-column justify-content-start w-100">
                                    <Col xs={12} className="column justify-content-start px-0">
                                        <p>NCR Code:</p>
                                    </Col>
                                    <Row xs={12} noGutters className="d-flex flex-row justify-content-start w-100">
                                        <Col xs={5}>
                                            <CheckboxForm
                                                onChange={(e) => onManualNcrChanged(e, form)}
                                                name="manualNcr"
                                                required={required.manualNcr}
                                                disabled={submitting}
                                                className="manual-rga-check-box"
                                                data={{
                                                    value: values?.manualNcr,
                                                    label: "Manual NCR",
                                                    disabled: submitting,
                                                }}
                                            />

                                        </Col>
                                        <Col xs={7}>
                                            <Fade in={values?.manualNcr} unmountOnExit mountOnEnter>
                                                <div style={{width: "100%"}}>
                                                    <NCRInput
                                                        name="ncr"
                                                        required={false}
                                                        value={values?.ncr ?? ""}
                                                        onChange={(e) => onNCRChanged(e, form)}
                                                        onDuplicateFound={onDuplicateFound}
                                                        disabled={submitting}
                                                        onValidFound={() => setDuplicateNCR(undefined)}
                                                        manual={values?.manualNcr}
                                                        setValidating={setValidatingNCR}
                                                        ref={ncrInput}
                                                    />
                                                </div>
                                            </Fade>
                                        </Col>
                                        <Col xs={12} className={'px-0'}>
                                            <Fade in={!!duplicateNCR} unmountOnExit mountOnEnter>
                                                <div className={'rounded-xxl my-3 p-3 d-flex align-items-center justify-content-between' +
                                                    ' background-secondary-color-4'}
                                                >
                                                    <p className={'duplicate-rga mr-2'}>
                                                        Report with code
                                                        <span className={'blue'}>
                                                            {duplicateNCR?.code ?? ''}
                                                        </span>
                                                        has been found. you may view its information by pressing this button.
                                                    </p>
                                                    <IconButton
                                                        color={'secondary'}
                                                        onPressed={openNRCInNewTab}
                                                    >
                                                        <OpenInNew className='icon normal'/>
                                                    </IconButton>
                                                </div>
                                            </Fade>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col xs={12} className="d-flex flex-column justify-content-start w-100">
                                    <Col xs={12} className="column justify-content-start px-0">
                                        <p>Submission Date:</p>
                                    </Col>
                                    <Row noGutters xs={12} className="d-flex flex-row justify-content-start w-100">
                                        <Col xs={5}>
                                            <CheckboxForm
                                                onChange={(e) => onTodaySubmissionChanged(e, form)}
                                                name="todaySubmission"
                                                disabled={submitting}
                                                required={required.todaySubmission}
                                                className="manual-rga-check-box"
                                                data={{
                                                    value: values?.todaySubmission,
                                                    label: "Today",
                                                    disabled: submitting,
                                                }}
                                            />
                                        </Col>
                                        <Col xs={7}>
                                            <Fade in={!values?.todaySubmission} unmountOnExit mountOnEnter>
                                                <div style={{width: "100%"}}>
                                                    <MuiRFF.DateTimePicker
                                                        name={"submissionDate"}
                                                        disabled={submitting}
                                                        required={required.submissionDate}
                                                    />
                                                </div>
                                            </Fade>
                                        </Col>
                                    </Row>
                                </Col>
                                {
                                    !!updating &&
                                    <Col xs={12} className="d-flex flex-column justify-content-start w-100 mt-3">
                                        <Col xs={12} className={'px-0'}>
                                            <p>RGA Number:</p>
                                        </Col>
                                        <Col xs={12} className={"mt-2 px-0"}>
                                            <RgaAutocomplete
                                                form
                                                name={'rgaNumber'}
                                                placeholder={'Search for rga\'s in the system...'}
                                                fullWidth
                                            />
                                        </Col>
                                    </Col>
                                }
                                <Col xs={12} className="d-flex flex-column justify-content-start w-100 mt-3">
                                    <Col xs={12} className={'px-0'}>
                                        <p>Reported By:</p>
                                    </Col>
                                    <Col xs={12} className={"mt-2 px-0"}>
                                        <OperatorAutocomplete
                                            form
                                            name={'reportedBy'}
                                            placeholder={'Search for operator...'}
                                            fullWidth
                                        />
                                    </Col>
                                </Col>
                                {/*<Col xs={12} className="d-flex flex-column justify-content-start w-100 mt-3">*/}
                                {/*    <Col xs={12} className={'px-0'}>*/}
                                {/*        <p>Assigned To:</p>*/}
                                {/*    </Col>*/}
                                {/*    <Col xs={12} className={"mt-2 px-0"}>*/}
                                {/*        <OperatorAutocomplete*/}
                                {/*            form*/}
                                {/*            name={'assignedTo'}*/}
                                {/*            placeholder={'Search for operator...'}*/}
                                {/*            fullWidth*/}
                                {/*        />*/}
                                {/*    </Col>*/}
                                {/*</Col>*/}
                                <Col xs={12} className="d-flex flex-column justify-content-start w-100 mt-3">
                                    <Col xs={12} className={'px-0'}>
                                        <p>Short Description:</p>
                                    </Col>
                                    <Col xs={12} className={"mt-2 px-0"}>
                                        <OutlinedInputForm
                                            fullWidth
                                            disabled={submitting}
                                            name={'description'}
                                            placeholder={'Type...'}
                                            required={required.description}
                                            multiline
                                            minRows={3}
                                        />
                                    </Col>
                                </Col>
                            </div>
                            <Divider className="divider mt-3 w-100"/>
                            <Row xs={12} className=" no-gutters footer justify-content-end align-items-center w-100">
                                <Button
                                    disabled={submitButtonDisabled}
                                    variant={"dialog"}
                                    borderRadius={"10px 0 10px 0"}
                                    color="primary"
                                    type="submit"
                                    onClick={() => proceedToNextStep(values)}
                                >
                                    Next
                                </Button>
                            </Row>
                        </Row>
                    );
                }}
            />
        </div>
    );
};

export default AddNCRBasicInfoSection;
